import { FC, useCallback, useMemo } from 'react';
import Text from '@/components/text';
import { Box, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { DateFormat, getNiceDate } from '@/lib/helpers';
import classNames from 'classnames';
import { UserEventMessage } from '@/lib/models/user-event';
import ContextHistory from './context-history';
import { capitalize } from 'lodash';
import ShortcutHistory from './shortcut-history';
import { getViolations } from '@/lib/models/user-event/helpers';
import {
  getToolMessageElementId,
  getToolUseIdFromMessage,
  isToolRole,
  ToolUsePair,
} from '@/lib/models/user-event/tools';
import Card from '@/components/card';
import MessageDetection from './message-detection';
import ToolContent from './tool-content';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import AgentLink from '@/components/agent-link';
import UserLink from '@/components/user-link';
import ServiceLink from '@/components/service-link';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import { getUserEventPublicService } from '@/lib/services/public-service.service';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { lighten } from '@mui/material/styles';
import { getUserEventAgent } from '@/lib/services/agent.service';
import { AgentMeta } from '@/lib/models/agent.model';
import Chip from '@/components/chip';
import ReadMore from '@/components/read-more';
import MessageMenu from './menu';

interface Props {
  message: UserEventMessage;
  userEvent: UserEventModel;
  isInFocusedGroup?: boolean;
  toolUsePair?: ToolUsePair;
}

const SCROLL_OFFSET = 48;

const useStyles = createUseStyles((theme: Theme) => ({
  messageContainer: {
    gap: 16,
    marginBottom: 16,
    position: 'relative',
    '&.user': {
      paddingLeft: 8,
      paddingRight: 0,
    },
    '&.assistant': {
      paddingLeft: 0,
      paddingRight: 8,
    },
    '&.inFocusedGroup': {
      marginBottom: 0,
      marginTop: 0,
    },
  },
  messageCard: {
    width: '85%',
    maxWidth: '85%',
    borderRadius: 16,
    border: 'none',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: 12,
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
    '&.user': {
      marginRight: 'auto',
      backgroundColor: lighten(theme.palette.primary.light, 0.9),
      borderTopLeftRadius: 2,
      '&::before': {
        left: -8,
        borderWidth: '8px 8px 8px 0',
        borderBottomColor: lighten(theme.palette.primary.light, 0.9),
      },
    },
    '&.assistant': {
      marginLeft: 'auto',
      backgroundColor: theme.palette.subtleGray,
      borderTopRightRadius: 2,
      '&::before': {
        right: -8,
        borderWidth: '8px 0 8px 8px',
        borderBottomColor: theme.palette.subtleGray,
      },
    },
  },
  cardContent: {
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    '&.user': {
      whiteSpace: 'pre-wrap',
    },
  },
  messageDate: {
    flexShrink: 0,
  },
}));

const Message: FC<Props> = ({ message, userEvent, isInFocusedGroup = false, toolUsePair }) => {
  const styles = useStyles();

  const {
    role,
    content,
    violations,
    origContent,
    timestamp,
    shortcut,
    contextDetails,
    sensitiveDataDetections = [],
    additionalProperties,
  } = message;

  const hasContent = !!content;
  const isEndUser = role === 'user';
  const isUser = ['user', 'system', 'tool_response'].includes(role);
  const isSystem = role === 'system';
  const isTool = ['tool_use', 'tool_response'].includes(role);

  const { serviceName, userClaim, customIntents, traceId, sensitiveDataRegexDetections } =
    userEvent;

  const psQuery = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data: publicServices = [] } = psQuery;
  const publicService = getUserEventPublicService(userEvent, publicServices);

  const agentsQuery = useAppData<AgentMeta[]>(AppDataQueryKey.Agents);
  const { data: agents = [] } = agentsQuery;
  const agent = getUserEventAgent(userEvent, agents);

  const nameMap = useMemo(() => {
    const assistantEl = serviceName ? (
      <Stack direction="row" gap={1}>
        {agent && (
          <Box width="60%">
            <AgentLink agent={agent} size="small" variant="outlined" condensed />
          </Box>
        )}
        <Stack direction="row" alignItems="center" gap={0.5}>
          <ServiceLink service={publicService} size="small" variant="outlined" condensed />
        </Stack>
      </Stack>
    ) : (
      'assistant'
    );
    const toolChip = (
      <Chip
        color="info"
        label={`TOOL ${role === 'tool_use' ? 'SELECTION' : 'RESPONSE'}`}
        size="small"
      />
    );

    const userLinkEl = <UserLink user={userClaim} showEmail={false} variant="outlined" condensed />;

    const userTitle = (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {userLinkEl}
        <Chip color="info" label="USER PROMPT" size="small" />
      </Stack>
    );

    const toolTitle = (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {role === 'tool_use' ? assistantEl : userLinkEl}
        {toolChip}
      </Stack>
    );

    const systemTitle = (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {userLinkEl}
        <Chip color="info" label="SYSTEM PROMPT" size="small" />
      </Stack>
    );

    const assistantTitle = (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {assistantEl}
        <Chip color="info" label="ASSISTANT RESPONSE" size="small" />
      </Stack>
    );

    return {
      context: 'context',
      user: userTitle,
      assistant: assistantTitle,
      system: systemTitle,
      tool_use: toolTitle,
      tool_response: toolTitle,
    };
  }, [serviceName, agent, publicService, role, userClaim]);

  const messageDate = getNiceDate(timestamp, DateFormat.TIMELINE);
  const violationsList = getViolations(violations);
  const hasViolations = isUser && !!violationsList.length;
  const hasTopics = isUser && isInFocusedGroup && !!customIntents.length;
  const hasRegexDetections = isUser && isInFocusedGroup && !!sensitiveDataRegexDetections.length;
  const hasSensitiveDataOnMessage = isUser && !!sensitiveDataDetections?.length;
  const showMessageDetection =
    hasViolations || hasTopics || hasRegexDetections || hasSensitiveDataOnMessage;
  const hasAdditionalProperties = !!additionalProperties;

  const { hasUse = false, hasResponse = false, toolName } = toolUsePair ?? {};

  const messageRootElId = useMemo(() => {
    // Today we only support tool messages, so we can return undefined for non-tool messages
    if (!isToolRole(role) || !hasAdditionalProperties) {
      return undefined;
    }
    const toolUseId = getToolUseIdFromMessage(message);
    if (!toolUseId) {
      return undefined;
    }
    return getToolMessageElementId(role, toolUseId);
  }, [hasAdditionalProperties, role, message]);

  const setMessageRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node !== null && messageRootElId !== undefined) {
        node.style.scrollMarginTop = `${SCROLL_OFFSET}px`;
      }
    },
    [messageRootElId]
  );

  const compAdditionalProperties = useMemo(() => {
    const { name = '' } = additionalProperties ?? {};
    if (!isTool || !hasAdditionalProperties) {
      return null;
    }
    if (role === 'tool_response' && toolName && !name) {
      return { ...additionalProperties, name: toolName };
    }
    return additionalProperties;
  }, [additionalProperties, hasAdditionalProperties, isTool, role, toolName]);

  const showToolContent = isTool && !!compAdditionalProperties;

  const containerClass = classNames(styles.messageContainer, {
    user: isUser,
    assistant: !isUser,
    inFocusedGroup: isInFocusedGroup,
  });

  const cardClass = classNames(styles.messageCard, {
    user: isUser,
    assistant: !isUser,
  });

  const contentClass = classNames(styles.cardContent, 'dd-privacy-mask', {
    user: isUser,
  });

  const ContentEl = useMemo(() => {
    if (shortcut?.id) {
      return <ShortcutHistory shortcut={shortcut} />;
    }

    if (contextDetails?.name) {
      return <ContextHistory contextDetails={contextDetails} content={content} />;
    }

    return (
      <ReadMore
        text={content}
        markdown={!isEndUser && !isSystem}
        maxLines={isSystem ? 8 : undefined}
      />
    );
  }, [shortcut, contextDetails, isEndUser, isSystem, content]);

  return (
    <Stack
      className={containerClass}
      id={messageRootElId}
      ref={messageRootElId !== undefined ? setMessageRootRef : undefined}
    >
      <Card className={cardClass} size="small">
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1}
            gap={1}
          >
            <Stack direction="row" alignItems="center" gap={0.5} minWidth={0} flex={1} width="100%">
              <Text bold size="small" dotdot>
                {nameMap[role] === 'context'
                  ? `${capitalize(contextDetails.origin)} Context`
                  : nameMap[role]}
              </Text>

              <MessageMenu
                message={message}
                traceId={traceId}
                hasToolUsePair={hasUse && hasResponse}
              />
            </Stack>
            <Text color="grey" size="small" className={styles.messageDate}>
              {messageDate.formatted}
            </Text>
          </Stack>

          {showMessageDetection && (
            <MessageDetection
              violationsList={violationsList}
              customIntents={customIntents}
              originalMessage={origContent || content}
              detections={sensitiveDataDetections}
              regexDetections={sensitiveDataRegexDetections}
            />
          )}

          {showToolContent && <ToolContent additionalProperties={compAdditionalProperties} />}

          {hasContent && (
            <Box className={contentClass} p={0.5}>
              {ContentEl}
            </Box>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default Message;
